import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { isNil } from 'lodash';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Switch from '@material-ui/core/Switch';
import SettingsCard from 'components/@common/Cards/SettingsCard';
import GET_KEYBOARDING_STUDENT_SETTINGS from 'graph/settings/key/get-keyboarding-student-settings-query';
import UPDATE_KEY_STUDENT_SKIP_JUMP_IN_SETTING from 'graph/settings/key/update-key-student-skip-jump-in-setting-mutation';
import { updateCache } from 'graph/update-cache';
import useStore from 'store';
import { KeySettingSkipJumpInData, Student } from 'types/schema-generated-types';
import getSettingSuccessAlert from '../../../utils/settings-success-alert';

interface JumpIntoKeyboardingProps {
  student: Student;
  productCurriculumCode: string;
  setting: KeySettingSkipJumpInData & { [key: string]: keyof KeySettingSkipJumpInData };
  expanded: string | null;
  handleChangeExpanded: (panel: string | null) => () => void;
}

const DEFAULT_ON_CURRICULUM_CODES = ['KEY3', 'KEY4', 'KEY5'];

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      padding: theme.spacing(2)
    },
    label: {
      color: theme.palette.common.black,
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: theme.spacing(1),
      marginLeft: theme.spacing(-1.3),
      '&.Mui-focused': {
        color: `${theme.palette.common.black} !important`
      }
    }
  };
});

const getStudentKeySettingsMutationCacheUpdate = (studentId: number): any => ({
  update: updateCache<any>([
    {
      query: GET_KEYBOARDING_STUDENT_SETTINGS,
      variables: {
        where: {
          studentId
        }
      },
      reducer: (cachedData, { updateKeyStudentSettingSkipJumpIn }): { keyStudentSettings: any } => {
        const [{ value: skipJumpIn }] = updateKeyStudentSettingSkipJumpIn;
        return {
          keyStudentSettings: {
            ...cachedData?.keyStudentSettings,
            skipJumpIn
          }
        };
      }
    }
  ])
});

const JumpIntoKeyboarding: FC<JumpIntoKeyboardingProps> = ({
  student,
  productCurriculumCode,
  setting,
  expanded,
  handleChangeExpanded
}) => {
  const { t } = useTranslation('JumpIntoKeyboardingSetting');
  const classes = useStyles();
  const [, { openAlert }] = useStore();

  const [updateKeyStudentSettingSkipJumpIn] = useMutation(
    UPDATE_KEY_STUDENT_SKIP_JUMP_IN_SETTING,
    getStudentKeySettingsMutationCacheUpdate(student.id)
  );

  const initialValue = useMemo(() => {
    if (isNil(setting?.[productCurriculumCode])) {
      return DEFAULT_ON_CURRICULUM_CODES.includes(productCurriculumCode);
    }
    return !!setting?.[productCurriculumCode];
  }, [setting, productCurriculumCode]);

  const [enableJumpIntoKeyboarding, setEnableJumpIntoKeyboarding] = useState(initialValue);

  const handleToggle = (): void => setEnableJumpIntoKeyboarding(!enableJumpIntoKeyboarding);
  const handleOnCancel = (): void => setEnableJumpIntoKeyboarding(initialValue);

  const handleOnConfirm = async (): Promise<void> => {
    try {
      await updateKeyStudentSettingSkipJumpIn({
        variables: {
          data: {
            studentId: student.id,
            value: { productCurriculumCode, enabled: enableJumpIntoKeyboarding }
          }
        }
      });
      openAlert(getSettingSuccessAlert('Skip First 6 Weeks', t));
    } catch (error) {
      //TODO
      console.log('Error');
    }
  };

  return (
    <SettingsCard
      title={t('Skip First 6 Weeks')}
      description={t(
        'Enable this setting to skip first six weeks of foundational skills and start the student at week 7 (Enabled by default for grades 3-5).'
      )}
      expanded={expanded}
      panel="jumpIntoKeyboarding"
      handleChangeExpanded={handleChangeExpanded}
      onCancel={handleOnCancel}
      onSubmit={handleOnConfirm}
    >
      <FormControl component="fieldset" className={classes.container}>
        <FormLabel component="label" className={classes.label}>
          Skip First 6 Weeks
        </FormLabel>
        <FormControlLabel
          control={
            <Switch
              checked={!!enableJumpIntoKeyboarding}
              onChange={handleToggle}
              name="JumpIntoKeyboarding"
              color="primary"
            />
          }
          label={enableJumpIntoKeyboarding ? t('Enabled') : t('Disabled')}
        />
      </FormControl>
    </SettingsCard>
  );
};

export default JumpIntoKeyboarding;
